import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Typography,
  Chip,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Switch,
  FormControlLabel
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  FileCopy,
  Add,
  FormatBold,
  FormatItalic,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Image as ImageIcon,
  Close
} from "@material-ui/icons";

import toastError from "../../errors/toastError";
import { findAllQueues } from "../../services/queue.api";
import { listAvailableServices } from "../../services/webForms.api";
import { WEB_FORM_FIELD_CATALOG } from "../../constants/webFormFields";
import { useWebFormBuilder } from "./useWebFormBuilder";
import FieldListEditor from "./FieldListEditor";
import FormPreview from "./FormPreview";
import ConfirmationPreview from "./ConfirmationPreview";

const useStyles = makeStyles(theme => ({
  palette: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  installBox: {
    background: "#1e1e2e",
    color: "#f8f8f2",
    padding: theme.spacing(2),
    borderRadius: 8,
    fontFamily: "monospace",
    fontSize: 13,
    wordBreak: "break-all",
    whiteSpace: "pre-wrap"
  }
}));

const API_BASE =
  window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * El Builder de formularios web (doc 0417 §8) — un MODAL, no una página:
 * mismo patrón que `WhatsAppModal`/`WhatsAppOficialModal`/
 * `WhatsAppFacebookModal` (los tres modales que ya usa "Agregar Conexión"),
 * abierto y cerrado por `Connections/index.js` vía `ConnectionModals.js`. La
 * corrección de diseño (2026-09-19): la primera versión abría una página
 * nueva (`/web-forms/new`) — se descartó por romper el patrón ya establecido
 * en todo el módulo Conexiones, que Kommo también sigue (modal, no página).
 */
const WebFormModal = ({ open, onClose, webFormId, onSave }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [queues, setQueues] = useState([]);
  const [services, setServices] = useState([]);
  const builder = useWebFormBuilder({ webFormId, open });
  const hadHashRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTab(0);
    hadHashRef.current = false;
    (async () => {
      try {
        const { data } = await findAllQueues();
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
    // Catálogo real de Agenda de Citas — se carga una sola vez al abrir el
    // Builder, para que la vista previa (campo "servicio") muestre la MISMA
    // lista que después verá el visitante. Lista vacía si la empresa no
    // configuró ningún servicio: no es un error, el campo sigue siendo
    // texto libre.
    (async () => {
      try {
        const { data } = await listAvailableServices();
        setServices(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  // Al crear (pasa de sin-hash a con-hash), salta directo a "Instalación" —
  // es el momento "aha" que el propio 0417 describe para el Builder de Kommo.
  useEffect(() => {
    if (builder.hash && !hadHashRef.current) {
      hadHashRef.current = true;
      setTab(2);
    }
  }, [builder.hash]);

  const installSnippet = builder.hash
    ? `<script src="${API_BASE}/api/v1/public/forms/embed.js" data-form="${builder.hash}" async></script>`
    : null;

  const copyInstallSnippet = () => {
    if (!installSnippet) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(installSnippet).then(
        () => toast.success("Script copiado."),
        () => toast.info(installSnippet)
      );
    } else {
      toast.info(installSnippet);
    }
  };

  const handleSave = () => {
    builder.handleSubmit(() => {
      if (onSave) onSave();
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      <DialogTitle>
        {builder.isEditing ? `Editar: ${builder.form.name || "Formulario web"}` : "Nuevo formulario web"}
      </DialogTitle>

      <DialogContent dividers>
        {builder.loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TextField
              fullWidth
              label="Nombre del formulario"
              value={builder.form.name}
              onChange={e => builder.set("name", e.target.value)}
              style={{ marginBottom: 16 }}
            />

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 16 }}
            >
              <Tab label="Campos y estilo" />
              <Tab label="Configuración" />
              <Tab label="Instalación" disabled={!builder.isEditing} />
            </Tabs>

            {tab === 0 && (
              <Box display="flex" gridGap={24} flexWrap="wrap">
                <Box minWidth={200}>
                  <Typography variant="subtitle2" gutterBottom>
                    Paleta de campos
                  </Typography>
                  <Box className={classes.palette}>
                    {WEB_FORM_FIELD_CATALOG.map(f => (
                      <Chip
                        key={f.tipo}
                        icon={<Add />}
                        label={f.label}
                        onClick={() => builder.addField(f.tipo)}
                        clickable
                        style={{ justifyContent: "flex-start" }}
                      />
                    ))}
                  </Box>

                  <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                    Estilo
                  </Typography>
                  <Box display="flex" flexDirection="column" gridGap={8}>
                    <TextField
                      label="Color de fondo"
                      type="color"
                      value={builder.form.styleJson.backgroundColor || "#ffffff"}
                      onChange={e => builder.setStyle("backgroundColor", e.target.value)}
                    />
                    <TextField
                      label="Color del botón"
                      type="color"
                      value={builder.form.styleJson.buttonColor || "#25D366"}
                      onChange={e => builder.setStyle("buttonColor", e.target.value)}
                    />
                  </Box>

                  <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                    Logo (opcional)
                  </Typography>
                  {builder.form.styleJson.logoUrl ? (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <img
                        src={builder.form.styleJson.logoUrl}
                        alt="Logo del formulario"
                        style={{ height: 40, maxWidth: 120, objectFit: "contain", borderRadius: 4 }}
                      />
                      <IconButton size="small" onClick={builder.removeLogo}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={<ImageIcon />}
                      disabled={builder.uploadingLogo}
                    >
                      {builder.uploadingLogo ? "Subiendo..." : "Subir logo"}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={e => builder.handleUploadLogo(e.target.files[0])}
                      />
                    </Button>
                  )}
                </Box>

                <Box flex={1} minWidth={280}>
                  <Typography variant="subtitle2" gutterBottom>
                    Campos del formulario — arrastra para reordenar
                  </Typography>
                  <FieldListEditor
                    fields={builder.form.fieldsJson}
                    onUpdate={builder.updateField}
                    onRemove={builder.removeField}
                    onReorder={builder.reorderFields}
                  />
                </Box>

                <Box minWidth={320} flex={1}>
                  <FormPreview
                    name={builder.form.name}
                    fields={builder.form.fieldsJson}
                    styleJson={builder.form.styleJson}
                    services={services}
                  />
                  {builder.form.fieldsJson.some(f => f.tipo === "servicio") && (
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                      {services.length > 0
                        ? `El campo "Servicio" se mostrará como lista desplegable con los ${services.length} servicios activos de Agenda de Citas.`
                        : 'No hay servicios configurados en Agenda de Citas — el campo "Servicio" se mostrará como texto libre.'}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {tab === 1 && (
              <Box display="flex" gridGap={24} flexWrap="wrap">
              <Box display="flex" flexDirection="column" gridGap={16} flex={1} minWidth={280}>
                <Autocomplete
                  options={queues}
                  getOptionLabel={q => q.name || ""}
                  value={queues.find(q => q.id === builder.form.queueId) || null}
                  onChange={(_, value) => builder.set("queueId", value ? value.id : "")}
                  noOptionsText="Sin resultados"
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Departamento (opcional)"
                      helperText="El ticket que se cree a partir de un envío se asigna a este departamento."
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={
                    builder.form.tagName
                      ? builder.form.tagName.split(",").map(t => t.trim()).filter(Boolean)
                      : []
                  }
                  onChange={(_, value) => builder.set("tagName", value.join(", "))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Etiquetas automáticas (opcional)"
                      placeholder="Escribe una etiqueta y presiona Enter"
                      helperText="Se agregan al ticket cuando alguien completa el formulario. Puedes poner varias."
                    />
                  )}
                />
                <Box>
                  <Box display="flex" alignItems="center" style={{ gap: 4, marginBottom: 4 }}>
                    <IconButton
                      size="small"
                      onClick={() => builder.setStyle("confirmationBold", !builder.form.styleJson.confirmationBold)}
                      color={builder.form.styleJson.confirmationBold ? "primary" : "default"}
                    >
                      <FormatBold fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => builder.setStyle("confirmationItalic", !builder.form.styleJson.confirmationItalic)}
                      color={builder.form.styleJson.confirmationItalic ? "primary" : "default"}
                    >
                      <FormatItalic fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => builder.setStyle("confirmationAlign", "left")}
                      color={
                        (builder.form.styleJson.confirmationAlign || "left") === "left" ? "primary" : "default"
                      }
                    >
                      <FormatAlignLeft fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => builder.setStyle("confirmationAlign", "center")}
                      color={builder.form.styleJson.confirmationAlign === "center" ? "primary" : "default"}
                    >
                      <FormatAlignCenter fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => builder.setStyle("confirmationAlign", "right")}
                      color={builder.form.styleJson.confirmationAlign === "right" ? "primary" : "default"}
                    >
                      <FormatAlignRight fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    fullWidth
                    label="Mensaje de confirmación"
                    multiline
                    rows={2}
                    value={builder.form.confirmationMessage}
                    onChange={e => builder.set("confirmationMessage", e.target.value)}
                  />
                </Box>
                <TextField
                  select
                  label="Destino del botón tras enviar"
                  value={builder.form.ctaMode}
                  onChange={e => builder.set("ctaMode", e.target.value)}
                >
                  <MenuItem value="url">Mi propia página (URL personalizada)</MenuItem>
                  <MenuItem value="whatsapp">Abrir WhatsApp con los datos que escribió</MenuItem>
                </TextField>
                {builder.form.ctaMode === "whatsapp" ? (
                  <Typography variant="caption" color="textSecondary">
                    El visitante hace clic y su propio WhatsApp se abre listo para mandar el
                    primer mensaje — con su nombre, servicio y fecha ya escritos. Usa el número
                    de la conexión configurada para Solicitudes Web (Configuración → Conexiones);
                    si esa conexión no está configurada, el botón simplemente no aparece.
                  </Typography>
                ) : (
                  <>
                    <TextField
                      label="URL de destino tras enviar (opcional)"
                      value={builder.form.redirectUrl}
                      onChange={e => builder.set("redirectUrl", e.target.value)}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!builder.form.autoRedirect}
                          onChange={e => builder.set("autoRedirect", e.target.checked)}
                          color="primary"
                          disabled={!builder.form.redirectUrl}
                        />
                      }
                      label="Redirigir automáticamente, sin mostrar nuestra tarjeta ni botón"
                    />
                    <Typography variant="caption" color="textSecondary" display="block">
                      {builder.form.autoRedirect
                        ? "El visitante va directo a esa URL — nunca ve el mensaje de confirmación ni ningún botón nuestro. Para clientes que ya tienen su propia página de gracias."
                        : "El visitante ve nuestra tarjeta de confirmación y hace clic en el botón de abajo cuando quiere continuar."}
                    </Typography>
                  </>
                )}
                {!(builder.form.ctaMode === "url" && builder.form.autoRedirect) && (
                  <TextField
                    label="Texto del botón de acción"
                    placeholder={builder.form.ctaMode === "whatsapp" ? "Conversar por WhatsApp" : "Continuar"}
                    helperText={
                      builder.form.ctaMode === "whatsapp"
                        ? "El visitante hace clic para abrir WhatsApp — no hay envío automático de nuestro lado."
                        : "El visitante hace clic para ir a la URL de arriba — no hay redirección automática."
                    }
                    value={builder.form.redirectButtonLabel}
                    onChange={e => builder.set("redirectButtonLabel", e.target.value)}
                    disabled={builder.form.ctaMode !== "whatsapp" && !builder.form.redirectUrl}
                  />
                )}

                {builder.isEditing && (
                  <>
                    <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                      ¿Dónde se va a instalar el script?
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Escribe cada página web donde el cliente va a pegar el script (pestaña
                      "Instalación"). Es una medida de seguridad: si alguien copia el script y lo
                      pega en OTRA página que no esté en esta lista, no va a funcionar ahí.
                    </Typography>
                    <Box display="flex" style={{ gap: 8 }}>
                      <TextField
                        placeholder="ej: midominio.com"
                        value={builder.newDomain}
                        onChange={e => builder.setNewDomain(e.target.value)}
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        onClick={builder.handleAddDomain}
                        disabled={builder.savingDomain || !builder.newDomain.trim()}
                      >
                        Agregar
                      </Button>
                    </Box>
                    <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                      {builder.domains.map(domain => (
                        <Chip
                          key={domain}
                          label={domain}
                          onDelete={() => builder.handleRemoveDomain(domain)}
                          disabled={builder.savingDomain}
                        />
                      ))}
                      {builder.domains.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          Sin dominios permitidos todavía — el formulario no aceptará envíos hasta agregar al menos uno.
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>

              <Box minWidth={320} flex={1}>
                <ConfirmationPreview
                  confirmationMessage={builder.form.confirmationMessage}
                  redirectUrl={builder.form.redirectUrl}
                  redirectButtonLabel={builder.form.redirectButtonLabel}
                  ctaMode={builder.form.ctaMode}
                  autoRedirect={builder.form.autoRedirect}
                  styleJson={builder.form.styleJson}
                />
              </Box>
              </Box>
            )}

            {tab === 2 && builder.isEditing && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Pega este script en la landing del cliente
                </Typography>
                <Box className={classes.installBox}>{installSnippet}</Box>
                <Button startIcon={<FileCopy />} onClick={copyInstallSnippet} style={{ marginTop: 8 }} variant="outlined">
                  Copiar script
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {builder.isEditing && (
          <Button onClick={builder.toggleStatus} disabled={builder.saving} style={{ marginRight: "auto" }}>
            {builder.form.status === "activo" ? "Pausar" : "Activar"}
          </Button>
        )}
        <Button onClick={onClose} disabled={builder.saving} color="secondary" variant="outlined">
          Cerrar
        </Button>
        <Button onClick={handleSave} disabled={builder.saving} color="primary" variant="contained">
          {builder.saving ? "Guardando..." : "Guardar"}
          {builder.saving && <CircularProgress size={18} style={{ marginLeft: theme.spacing(1) }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebFormModal;
